import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import media from "styled-media-query";
import { ChallengeScheme, CoorporateScheme, UserScheme } from "client-v2";
import { useSelector } from "react-redux";
import { State } from "../../redux";

import { Modal, Text, Button, ColorV2 } from "@adoptaunabuelo/react-components";
import { Download, X } from "lucide-react";
import LetterCanvas, { CanvasRef } from "../canvas/LetterCanvas";
import DonationCanvas from "../canvas/DonationCanvas";
import ChallengeCanvas from "../canvas/ChallengeCanvas";

const Row = styled.div`
	display: flex;
	flex-direction: row;
	${media.lessThan("medium")`
        flex-direction: column;
        align-items: center;
    `}
`;
const Column = styled.div`
	display: flex;
	flex-direction: column;
`;
const EditContainer = styled.div`
	display: flex;
	flex-direction: column;
	position: sticky;
	bottom: 0px;
	padding: 24px 0px 0px;
	border-top: 1px solid ${ColorV2.border.neutralSoft};
	margin-top: 24px;
	gap: 24px;
	background-color: white;
	${media.lessThan("medium")`
        padding: 24px 0px 8px;
    `}
`;
const Cell = styled.div`
	display: flex;
	flex-direction: row;
`;
const ButtonCell = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	background-color: ${ColorV2.surface.background};
	padding: 8px 16px;
	border-radius: 16px;
	${media.lessThan("medium")`
        padding: 8px 12px;
    `}
`;
const ButtonContainer = styled.div`
	display: flex;
	gap: 4px;
`;
const Number = styled(Text)`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 24px;
	width: 24px;
	border-radius: 30px;
	font-weight: 600 !important;
`;
const ImageContainer = styled.div`
	width: 250px;
	aspect-ratio: 46/81;
	background-color: white;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.18);
	${media.lessThan("small")`
        width: 180px;
    `}
`;
const ImageColumn = styled.div`
	display: flex;
	flex-direction: column;
	background-color: ${ColorV2.surface.background};
	padding: 32px;
	justify-content: center;
	${media.lessThan("medium")`
        width: 100%;
		align-items: center;
    `}
`;
const CustomizationButton = styled.button<{ $isSelected: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 32px;
	width: 32px;
	border-radius: 32px;
	padding: 0px;
	cursor: pointer;
	background-color: transparent;
	border: ${(props) =>
		props.$isSelected ? "2px solid " + ColorV2.text.neutralHard : "none"};
	&:hover {
		background-color: ${ColorV2.surface.neutralSoft};
	}
`;

const InstagramModal = (props: Props) => {
	const canvasRef = useRef<CanvasRef>(null);
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const [loading, setLoading] = useState(false);
	const [instagramImage, setInstagramImage] = useState<string | undefined>(
		undefined
	);
	const [customization, setCustomization] = useState(props.type === "challenge" ? {
		design: "A",
		color: "008FF5",
	} : {
		design: "A",
		color: "00315C",
	});
	const [customOptions, setCustomOptions] = useState<
		{
			id: string;
			type: string;
			title: string;
		}[]
	>([]);

	useEffect(() => {
		loadImage(customization.design, customization.color);
	}, []);

	useEffect(() => {
		if (props.type === "donation") {
			setCustomOptions([
				{
					id: "primary",
					type: "color",
					title: "008FF5",
				},
				{
					id: "primaryHard",
					type: "color",
					title: "00315C",
				},
				{
					id: "background",
					type: "color",
					title: "F9F6F3",
				},
			]);
		} else if (props.type === "letter") {
			setCustomOptions([
				{
					id: "a",
					type: "design",
					title: "A",
				},
				{
					id: "b",
					type: "design",
					title: "B",
				},
				{
					id: "c",
					type: "design",
					title: "C",
				},
				{
					id: "primary",
					type: "color",
					title: "008FF5",
				},
				{
					id: "primaryHard",
					type: "color",
					title: "00315C",
				},
				{
					id: "background",
					type: "color",
					title: "F9F6F3",
				},
			]);
		}
	}, [props.type]);

	const loadImage = async (design: string, color: string) => {
		setInstagramImage(
			require(`../../assets/images/storie/${props.type}_${design}_${color}.png`)
		);
	};

	const downloadImage = async () => {
		setLoading(true);
		const base64 = canvasRef.current?.toDataURL();
		if (base64) {
			//Transform base64 into file
			const response = await fetch(base64);
			const blob = await response.blob();
			const file = new File([blob], "ig_stories.png", {
				type: "image/png",
			});

			//Open mobile share options
			if (navigator.canShare && navigator.canShare({ files: [file] })) {
				try {
					await navigator.share({
						files: [file],
						title: "¡Ya soy embajador de Adopta Un Abuelo!",
						text: "Nos necesitan para seguir acompañando a personas mayores",
					});
					setLoading(false);
				} catch (error) {
					setLoading(false);
				}
			} else {
				//No share options. Check if it is a mobile or desktop devise
				const isMobile = /iPhone|iPad|iPod|Android/i.test(
					navigator.userAgent
				);
				if (!isMobile) {
					//Is a desktop. Direct download
					const downloadLink = document.createElement("a");
					const fileName = "ig_stories.png";
					downloadLink.href = base64;
					downloadLink.download = fileName;
					downloadLink.click();
					setLoading(false);
				} else {
					//Is a mobile. Create a full screen window
					const newWindow = window.open();
					if (newWindow) {
						newWindow.document.write(
							`<html><head><title>Descarga</title></head><body style="margin:0;"><img src="${base64}" style="width:100%;"/></body></html>`
						);
						setLoading(false);
					} else {
						alert(
							"Tu navegador ha bloqueado la apertura de una nueva pestaña. Por favor, habilítala para descargar la imagen."
						);
						setLoading(false);
					}
				}
			}
		}
	};

	const onCustomizationChange = (item: {
		id: string;
		type: string;
		title: string;
	}) => {
		const temp = {
			design: item.type === "design" ? item.title : customization.design,
			color: item.type === "color" ? item.title : customization.color,
		};
		setCustomization(temp);
		loadImage(temp.design, temp.color);
	};

	return (
		<Modal
			isVisible={props.isVisible}
			onClose={props.onClose}
			type={isMobile ? "full-screen" : "default"}
			style={{
				backgroundColor: "white",
				width: 800,
				padding: 0,
				maxHeight: "95%",
			}}
			contentStyle={{ padding: 0 }}
			hideHeader={true}
			hideClose={true}
		>
			<Button
				style={{ position: "absolute", top: 8, right: 8 }}
				design={"image"}
				icon={<X color={ColorV2.text.neutralHard} />}
				onClick={props.onClose}
			/>
			<Row>
				<ImageColumn>
					<ImageContainer>
						{instagramImage &&
							(props.type === "letter" ? (
								<LetterCanvas
									ref={canvasRef}
									style={{ width: "100%" }}
									image={instagramImage}
									name={props.grandpa.name}
									birthday={props.grandpa.birthday?.iso}
									design={customization.design}
									textColor={
										customization.color === "F9F6F3"
											? ColorV2.text.neutralHard
											: "white"
									}
								/>
							) : props.type === "donation" ? (
								<DonationCanvas
									ref={canvasRef}
									style={{ width: "100%" }}
									image={instagramImage}
									design={customization.design}
									textColor={
										customization.color === "F9F6F3"
											? ColorV2.text.neutralHard
											: "white"
									}
								/>
							) : props.type === "challenge" ? (
								<ChallengeCanvas
									ref={canvasRef}
									style={{ width: "100%" }}
									image={instagramImage}
									design={customization.design}
									challenge={props.challenge}
									textColor={ColorV2.text.neutralHard}
								/>
							) : null)}
					</ImageContainer>
				</ImageColumn>
				<Column
					style={{
						justifyContent: "center",
						padding: isMobile ? "24px 24px 0px" : "48px 24px 24px",
					}}
				>
					<Text
						type="h3"
						weight="semibold"
						style={{ marginBottom: 32 }}
					>
						Cómo compartir la imagen en Instagram
					</Text>
					<Column style={{ gap: 18 }}>
						<Cell style={{ gap: 12 }}>
							<Number type="p2">1</Number>
							<Text type="p" style={{ flex: 1 }}>
								Guarda la imagen en tu galería.
							</Text>
						</Cell>
						<Cell style={{ gap: 12 }}>
							<Number type="p2">2</Number>
							<Text type="p" style={{ flex: 1 }}>
								Crea un story en Instagram y usa la imagen que
								acabas de descargar.
							</Text>
						</Cell>
						<Cell style={{ gap: 12 }}>
							<Number type="p2">3</Number>
							<Text type="p" style={{ flex: 1 }}>
								Acuérdate de mencionarnos{" "}
								<span style={{ fontWeight: 600 }}>
									@adopta1abuelo
								</span>
								. ¡Nos encanta ver lo que subís!
							</Text>
						</Cell>
						<Cell style={{ gap: 12 }}>
							<Number type="p2">4</Number>
							<Text type="p" style={{ flex: 1 }}>
								¡Y a compartir! Te agradecemos mucho que animes
								a otras personas a colaborar. ¡Eres increíble!
							</Text>
						</Cell>
					</Column>
					<EditContainer>
						{customOptions.filter((i) => i.type === "design")
							.length > 0 && (
							<ButtonCell>
								<Text type="p">Elige tu diseño</Text>
								<ButtonContainer>
									{customOptions
										.filter((i) => i.type === "design")
										.map((item) => {
											const isSelected =
												item.title ===
												customization.design;
											return (
												<CustomizationButton
													key={item.id}
													$isSelected={isSelected}
													onClick={() =>
														onCustomizationChange(
															item
														)
													}
												>
													<Text
														type="p"
														weight="medium"
													>
														{item.title}
													</Text>
												</CustomizationButton>
											);
										})}
								</ButtonContainer>
							</ButtonCell>
						)}
						{customOptions.filter((i) => i.type === "color")
							.length > 0 && (
							<ButtonCell>
								<Text type="p">Selecciona un color</Text>
								<ButtonContainer>
									{customOptions
										.filter((i) => i.type === "color")
										.map((item) => {
											const isSelected =
												item.title ===
												customization.color;
											return (
												<CustomizationButton
													key={item.id}
													$isSelected={isSelected}
													onClick={() =>
														onCustomizationChange(
															item
														)
													}
												>
													<div
														style={{
															height: 20,
															width: 20,
															borderRadius: 20,
															backgroundColor:
																"#" +
																item.title,
														}}
													/>
												</CustomizationButton>
											);
										})}
								</ButtonContainer>
							</ButtonCell>
						)}
						<Button icon={<Download />} onClick={downloadImage}>
							Guardar imagen
						</Button>
					</EditContainer>
				</Column>
			</Row>
		</Modal>
	);
};
export default InstagramModal;
export type Props = {
	isVisible: boolean;
	onClose: () => void;
} & InstagramModalProps;
export type InstagramModalProps =
	| {
			type: "letter";
			grandpa: UserScheme;
			coorporate?: CoorporateScheme;
	  }
	| {
			type: "donation";
	  }
	| {
			type: "register";
	  }
	| {
			type: "challenge";
			challenge: ChallengeScheme;
	  };
